import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useStory } from '../context/StoryContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { generateImage } from '../services/aiService';
import QuestManager from '../components/story/QuestManager';
import ArchivePanel from '../components/story/ArchivePanel';
import StoryIdeas from '../components/story/StoryIdeas';
import ImageGallery from '../components/story/ImageGallery';
import DiceRoller from '../components/story/DiceRoller';
import EmojiPicker from 'emoji-picker-react';
import RecoveryPanel from '../components/story/RecoveryPanel';
import CombatOverlay from '../components/story/CombatOverlay';

// Sidebar tabs — Active story-specific options
const TABS = [
  { id: 'quests', label: 'Quests', icon: FiIcons.FiTarget },
  { id: 'chapters', label: 'Chapters', icon: FiIcons.FiBookOpen },
  { id: 'ideas', label: 'Ideas', icon: FiIcons.FiZap },
  { id: 'gallery', label: 'Gallery', icon: FiIcons.FiImage },
  { id: 'recovery', label: 'Recovery', icon: FiIcons.FiTrash2 },
];

const StoryView = () => {
  const { state, addStoryEntry, editStoryEntry, toggleEntryBookmark, toggleArchivedEntryBookmark, deleteStoryEntry, editArchivedEntry, deleteArchivedEntry, startOverChapter, setGenerating, updateWorld, setSuggestions, addToGallery, importStoryState, submitPassage, updateStoryTitle, archiveCurrentStory, toggleAiPause, sendPoke, startCombat } = useStory();
  const { story, world, isGenerating, ai, narrativeStyle, character, storyTitle } = state;

  // Debug effect to expose combat start to console for e2e test
  useEffect(() => {
    window._triggerCombatForTest = () => {
      startCombat({ name: 'Goblin', stats: { level: 2, health: 30, maxHealth: 30 } });
    };
  }, [startCombat]);
  const { user } = useAuth();

  const isOwner = state.ownerId === user?.id;

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab');

  let isAiPausedForUser = ai?.settings?.isPaused;
  if (user && state.collaborators) {
      const userCollab = state.collaborators.find(c => c.user_id === user.id);
      if (userCollab && userCollab.ai_paused !== undefined) {
          isAiPausedForUser = userCollab.ai_paused;
      }
  }
  const [input, setInput] = useState('');
  const [localTitle, setLocalTitle] = useState(storyTitle || `Chapter ${state.archive.length + 1}`);

  useEffect(() => {
    setLocalTitle(storyTitle || `Chapter ${state.archive.length + 1}`);
  }, [storyTitle, state.archive.length]);
  const [activeTab, setActiveTab] = useState(urlTab || 'quests');

  useEffect(() => {
    if (urlTab) setActiveTab(urlTab);
  }, [urlTab]);

  const [viewingArchiveChapter, setViewingArchiveChapter] = useState(null);

  useEffect(() => {
    const handleViewChapter = (e) => {
      if (e.detail && e.detail.chapter) {
        setViewingArchiveChapter(e.detail.chapter);
        if (window.innerWidth < 1024) { // lg breakpoint
          navigate('/story');
        }
      }
    };
    window.addEventListener('view-archive-chapter', handleViewChapter);
    return () => window.removeEventListener('view-archive-chapter', handleViewChapter);
  }, [navigate]);
  const [sceneImage, setSceneImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isReaderMode, setIsReaderMode] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);

  const canExport = state.subscriptionTier === 'Storyteller' || state.subscriptionTier === 'Worldbuilder';

  const prepareTabularData = () => {
    const data = [];
    state.story.forEach(entry => {
      data.push({
        Chapter: 'Current Active Session',
        Role: entry.role,
        Content: entry.content,
        Timestamp: new Date(entry.timestamp).toLocaleString()
      });
    });
    state.archive.forEach(chapter => {
      chapter.content.forEach(entry => {
         data.push({
            Chapter: chapter.title,
            Role: entry.role,
            Content: entry.content,
            Timestamp: new Date(entry.timestamp || chapter.timestamp).toLocaleString()
         });
      });
    });
    return data;
  };

  const handleExport = (format) => {
    if (!canExport) {
        alert("Story Export & Cloud Sync is available on the Storyteller tier and above. Please upgrade to export your story.");
        return;
    }

    const timestamp = new Date().getTime();

    if (format === 'json') {
      const jsonStr = JSON.stringify(state);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", `weaver_story_export_${timestamp}.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      URL.revokeObjectURL(url);
    } else {
      const data = prepareTabularData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Story Archive");

      if (format === 'csv') {
        XLSX.writeFile(workbook, `weaver_story_export_${timestamp}.csv`);
      } else if (format === 'xlsx') {
        XLSX.writeFile(workbook, `weaver_story_export_${timestamp}.xlsx`);
      }
    }
    setShowExportMenu(false);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const importedState = JSON.parse(event.target.result);
          if (window.confirm('Are you sure you want to import this story? It will be added to your current progress.')) {
            await importStoryState(importedState);
          }
        } catch (error) {
          if (window.confirm('Are you sure you want to import this text document? It will be added to your current progress.')) {
            const fallbackState = {
              story: [{ role: 'user', content: event.target.result, timestamp: new Date().toISOString() }]
            };
            await importStoryState(fallbackState);
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => scrollToBottom(), [story, isGenerating]);

  const handleSend = async (textToSubmit) => {
    if (!user) {
      alert("Please sign up for a free account to continue writing.");
      return;
    }
    const content = typeof textToSubmit === 'string' ? textToSubmit : input;
    if (!content.trim() || isGenerating) return;
    setInput('');
    await submitPassage(content, narrativeStyle);
  };

  return (
    <div className="flex h-[calc(100dvh-4rem)] bg-white dark:bg-purple-950 overflow-hidden relative">
      {world?.backgroundImage && (
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-10 bg-cover bg-center"
          style={{ backgroundImage: `url('${world.backgroundImage}')` }}
        />
      )}
      {/* Mobile Tab Overlay */}
      {urlTab && (
        <div className="absolute inset-0 z-40 bg-white dark:bg-purple-950 flex flex-col lg:hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-purple-800 bg-white dark:bg-purple-950">
             <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <SafeIcon icon={TABS.find(t => t.id === activeTab)?.icon} />
                <span>{TABS.find(t => t.id === activeTab)?.label}</span>
             </h2>
             <button onClick={() => navigate('/story')} className="p-2 text-gray-900 dark:text-purple-400 hover:text-amber-600 transition-colors">
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
             </button>
          </div>
          <div className="flex-1 overflow-y-auto story-scroll p-4 bg-gray-50/50 dark:bg-purple-900/20">
             {activeTab === 'quests' && <QuestManager />}
             {activeTab === 'chapters' && <ArchivePanel isSidebar={true} />}
             {activeTab === 'ideas' && isOwner && <StoryIdeas />}
             {activeTab === 'gallery' && <ImageGallery />}
             {activeTab === 'recovery' && <RecoveryPanel />}
          </div>
        </div>
      )}

      {/* Sidebar */}
      {!isReaderMode && (
      <div className="w-80 bg-gray-100/80 dark:bg-purple-900/80 border-r border-gray-200 dark:border-purple-800 hidden lg:flex flex-col shadow-2xl flex-shrink-0">

        {/* Tab Row — icon + label, all 6 fit without scrolling */}
        <div className="flex border-b border-gray-100 dark:border-purple-800 bg-white dark:bg-purple-950">
          {TABS.filter(t => t.id !== 'recovery' && (t.id !== 'ideas' || isOwner)).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
              className={`flex-1 flex flex-col items-center justify-center py-3 gap-0.5 transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-600 bg-amber-50 dark:bg-amber-900/10'
                  : 'border-transparent text-gray-900 dark:text-purple-400 hover:text-gray-900 dark:hover:text-gray-400 hover:bg-gray-50 dark:hover:bg-purple-900/30'
              }`}
            >
              <SafeIcon icon={tab.icon} className="text-sm" />
              <span className="text-[8px] font-black uppercase tracking-wide leading-none">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto story-scroll p-5">
          {activeTab === 'quests' && <QuestManager />}
          {activeTab === 'chapters' && <ArchivePanel isSidebar={true} />}
          {activeTab === 'ideas' && isOwner && <StoryIdeas />}
          {activeTab === 'gallery' && <ImageGallery />}
          {activeTab === 'recovery' && <RecoveryPanel />}
        </div>

        {/* World Events — always visible at bottom */}
        <div className="border-t border-gray-100 dark:border-purple-800 p-4 bg-gray-100/50 dark:bg-purple-900/30">
          <p className="text-[9px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest mb-3 flex items-center space-x-1.5">
            <SafeIcon icon={FiIcons.FiRadio} className="text-amber-500" />
            <span>World Pulse</span>
          </p>
          <div className="space-y-2 max-h-28 overflow-y-auto story-scroll mb-3">
            {(world.worldEvents || []).slice(0, 3).map((ev) => (
              <div key={ev.id} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-[9px] font-black text-gray-900 dark:text-purple-200 leading-tight">{ev.title}</p>
                  <p className="text-[8px] text-gray-900 dark:text-purple-400">
                    {ev.type}{narrativeStyle === 'adventurer' && ev.timeTrigger ? ` · ${ev.timeTrigger}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setActiveTab('recovery')}
            className={`w-full flex items-center justify-center space-x-2 py-2 rounded-lg text-xs font-bold transition-colors ${
              activeTab === 'recovery'
                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                : 'bg-white/50 dark:bg-purple-900/30 text-gray-900 dark:text-purple-400 hover:bg-gray-50 dark:hover:bg-purple-800'
            } border border-gray-200 dark:border-purple-800`}
          >
            <SafeIcon icon={FiIcons.FiTrash2} />
            <span>Recovery</span>
          </button>
        </div>

        {/* Active Collaborators */}
        {(isOwner || (state.collaborators && state.collaborators.filter(c => c.status === 'Accepted').length > 0)) && (
          <div className="border-t border-gray-100 dark:border-purple-800 p-4 bg-gray-100/50 dark:bg-purple-900/30 relative">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-1.5">
                <SafeIcon icon={FiIcons.FiUsers} className="text-amber-500" />
                <span>Active Characters</span>
              </p>
              <button
                onClick={() => setActiveTab('gallery')}
                className={`flex items-center space-x-1 px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'gallery'
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    : 'bg-white/50 dark:bg-purple-900/30 text-gray-900 dark:text-purple-400 hover:bg-gray-50 dark:hover:bg-purple-800'
                } border border-gray-200 dark:border-purple-800`}
                title="View Gallery"
              >
                <SafeIcon icon={FiIcons.FiImage} />
                <span>Gallery</span>
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Story Owner Avatar */}
              <div className="relative">
                <div
                  className={`w-8 h-8 rounded-full border-2 border-gray-200 dark:border-purple-700 overflow-hidden flex items-center justify-center bg-gray-100 dark:bg-purple-900 shadow-sm ${!isOwner ? 'cursor-pointer hover:ring-2 hover:ring-amber-500' : ''}`}
                  title={isOwner ? "You (Story Owner)" : "Story Owner - Click to Poke"}
                  onClick={async () => {
                    if (!isOwner && state.ownerId) {
                      const success = await sendPoke(state.ownerId);
                      if (success) {
                        window.dispatchEvent(new CustomEvent('app-notification', {
                          detail: {
                            title: 'Poke Sent!',
                            message: `You poked the Story Owner.`,
                            type: 'success'
                          }
                        }));
                      }
                    }
                  }}
                >
                  {character?.avatar ? (
                    <img src={character.avatar} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-bold text-gray-900 dark:text-purple-300">{character?.name?.charAt(0) || 'U'}</span>
                  )}
                </div>
                {state.onlineUsers?.includes(state.ownerId) && (
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border border-white rounded-full"></div>
                )}
              </div>

              {/* Collaborator Avatars */}
              {(state.collaborators || []).filter(c => c.status === 'Accepted').map(c => (
                <div key={c.id} className="relative">
                  <div
                    className={`w-8 h-8 rounded-full border-2 border-gray-200 dark:border-purple-700 overflow-hidden flex items-center justify-center bg-gray-100 dark:bg-purple-900 shadow-sm ${c.user_id && c.user_id !== user?.id ? 'cursor-pointer hover:ring-2 hover:ring-amber-500' : ''}`}
                    title={`${c.characters?.name || c.name || 'Unknown'} (${c.role})${c.user_id && c.user_id !== user?.id ? ' - Click to Poke' : ''}`}
                    onClick={async () => {
                      if (c.user_id && c.user_id !== user?.id) {
                        const success = await sendPoke(c.user_id);
                        if (success) {
                          window.dispatchEvent(new CustomEvent('app-notification', {
                            detail: {
                              title: 'Poke Sent!',
                              message: `You poked ${c.characters?.name || c.name || 'them'}.`,
                              type: 'success'
                            }
                          }));
                        }
                      }
                    }}
                  >
                    {c.characters?.avatar ? (
                      <img src={c.characters.avatar} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold text-gray-800 dark:text-purple-300">
                        {(c.characters?.name || c.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {c.user_id && state.onlineUsers?.includes(c.user_id) && (
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border border-white rounded-full"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Narrative Viewport */}
      <div className="flex-1 flex flex-col relative min-w-0">
        {/* Header Options */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between bg-white/10 dark:bg-[#0f0a1c]/80 backdrop-blur-md border-b border-transparent shadow-sm">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {viewingArchiveChapter ? (
              <span className="text-xl font-bold text-gray-900 dark:text-white px-1 py-0.5 max-w-full truncate" title={viewingArchiveChapter.title}>
                {viewingArchiveChapter.title}
              </span>
            ) : (
              state.ownerId === user?.id ? (
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-wider pl-1">Story Title</span>
                  <input
                    type="text"
                    value={localTitle}
                    onChange={(e) => setLocalTitle(e.target.value)}
                    onBlur={(e) => updateStoryTitle(e.target.value)}
                    className="bg-transparent border-b border-transparent hover:border-purple-300 focus:border-amber-500 text-xl font-bold text-gray-900 dark:text-white focus:outline-none transition-colors px-1 py-0.5 max-w-full truncate"
                    placeholder="Story Title..."
                  />
                </div>
              ) : (
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-wider pl-1">Story Title</span>
                  <span className="text-xl font-bold text-gray-900 dark:text-white px-1 py-0.5 max-w-full truncate" title={state.storyTitle}>
                    {state.storyTitle}
                  </span>
                </div>
              )
            )}
          </div>

          <div className="flex space-x-2 ml-4 flex-shrink-0">
            <button
              onClick={() => setIsReaderMode(!isReaderMode)}
              className="px-3 py-1.5 bg-white/50 dark:bg-purple-950/50 hover:bg-gray-50 dark:hover:bg-purple-900/30 text-gray-900 dark:text-purple-400 hover:text-gray-800 text-xs rounded-full border border-gray-200 dark:border-purple-800 transition-colors backdrop-blur-sm flex items-center space-x-1"
              title="Toggle Focus Mode"
            >
              <SafeIcon icon={isReaderMode ? FiIcons.FiMinimize2 : FiIcons.FiMaximize2} />
              <span>Focus</span>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto story-scroll p-6 sm:px-12 sm:pb-12 pt-24 sm:pt-32" style={{ WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)', maskImage: 'linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)' }}>
          <div className={`max-w-3xl mx-auto space-y-12 pb-48 ${narrativeStyle === 'writer' ? 'writer-prose' : ''}`}>
            {(viewingArchiveChapter ? viewingArchiveChapter.content : (story || [])).map((entry, idx) => (
              <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {entry.role === 'user' ? (
                  narrativeStyle === 'writer' ? (
                      <div className="w-full text-gray-500 dark:text-gray-400 font-serif italic mb-6 text-right group relative">
                        {editingMessageId === entry.id ? (
                          <div className="flex flex-col items-end gap-2 w-full max-w-lg ml-auto">
                            <textarea
                              value={editContent} onChange={(e) => setEditContent(e.target.value)}
                              className="w-full bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 p-2 rounded border border-purple-300 dark:border-purple-700 font-serif italic text-right focus:outline-none focus:ring-1 focus:ring-amber-500"
                              rows={3}
                            />
                            <div className="flex space-x-2">
                              <button onClick={() => setEditingMessageId(null)} className="text-xs text-gray-900 dark:text-purple-500 hover:text-purple-700">Cancel</button>
                              <button onClick={() => { editStoryEntry(entry.id, editContent); setEditingMessageId(null); }} className="text-xs text-amber-600 hover:text-amber-500 font-bold">Save</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="absolute top-0 right-0 -translate-y-full flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => viewingArchiveChapter ? toggleArchivedEntryBookmark(viewingArchiveChapter.id, entry.id, !entry.bookmarked) : toggleEntryBookmark(entry.id, !entry.bookmarked)} className={`p-1 ${entry.bookmarked ? 'text-amber-500' : 'text-gray-900 dark:text-purple-400 hover:text-amber-500'}`} title={entry.bookmarked ? "Remove Bookmark" : "Bookmark"}><SafeIcon icon={FiIcons.FiBookmark} className={entry.bookmarked ? 'fill-current text-xs' : 'text-xs'}/></button>
                              <button onClick={() => { setEditContent(entry.content); setEditingMessageId(entry.id); }} className="p-1 text-gray-900 dark:text-purple-400 hover:text-amber-500" title="Edit"><SafeIcon icon={FiIcons.FiEdit2} className="text-xs"/></button>
                              <button onClick={() => { if(window.confirm('Delete this entry?')) { viewingArchiveChapter ? deleteArchivedEntry(viewingArchiveChapter.id, entry.id) : deleteStoryEntry(entry.id); } }} className="p-1 text-gray-900 dark:text-purple-400 hover:text-red-500" title="Delete"><SafeIcon icon={FiIcons.FiTrash2} className="text-xs"/></button>
                            </div>
                            <span className="font-bold mr-2">
                              {entry.bookmarked && <SafeIcon icon={FiIcons.FiBookmark} className="inline mr-1 text-amber-500 fill-current text-[10px]" title="Bookmarked" />}
                              {entry.character_preferred_reference === 'nickname' && entry.character_nickname ? entry.character_nickname : (entry.character_name || entry.profiles?.username || 'User')}:
                            </span>
                            {entry.content}
                          </>
                        )}
                      </div>
                  ) : (
                      <div className="max-w-[85%] flex flex-col items-end group relative">
                         <div className="text-xs font-bold text-gray-600 mb-1">{entry.character_preferred_reference === 'nickname' && entry.character_nickname ? entry.character_nickname : (entry.character_name || entry.profiles?.username || 'User')}</div>
                         {editingMessageId === entry.id ? (
                           <div className="bg-purple-700 p-4 rounded-2xl rounded-tr-sm shadow-xl border border-purple-500 w-full min-w-[300px]">
                              <textarea
                                value={editContent} onChange={(e) => setEditContent(e.target.value)}
                                className="w-full bg-transparent text-white focus:outline-none resize-none leading-relaxed"
                                rows={Math.max(1, editContent.split('\n').length)}
                              />
                              <div className="flex justify-end space-x-3 mt-2">
                                <button onClick={() => setEditingMessageId(null)} className="text-xs text-gray-900 hover:text-white dark:text-purple-300">Cancel</button>
                                <button onClick={() => { editStoryEntry(entry.id, editContent); setEditingMessageId(null); }} className="text-xs text-amber-400 hover:text-amber-300 font-bold">Save</button>
                              </div>
                           </div>
                         ) : (
                           <>
                              <div className="absolute top-0 right-0 -translate-y-full flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity mb-1">
                                <button onClick={() => viewingArchiveChapter ? toggleArchivedEntryBookmark(viewingArchiveChapter.id, entry.id, !entry.bookmarked) : toggleEntryBookmark(entry.id, !entry.bookmarked)} className={`p-1.5 bg-white dark:bg-purple-800 rounded-full shadow-sm ${entry.bookmarked ? 'text-amber-500' : 'text-gray-900 dark:text-purple-300 hover:text-amber-500'}`} title={entry.bookmarked ? "Remove Bookmark" : "Bookmark"}><SafeIcon icon={FiIcons.FiBookmark} className={entry.bookmarked ? 'fill-current text-xs' : 'text-xs'}/></button>
                                <button onClick={() => { setEditContent(entry.content); setEditingMessageId(entry.id); }} className="p-1.5 bg-white dark:bg-purple-800 rounded-full text-gray-900 dark:text-purple-300 hover:text-amber-500 shadow-sm" title="Edit"><SafeIcon icon={FiIcons.FiEdit2} className="text-xs"/></button>
                                <button onClick={() => deleteStoryEntry(entry.id)} className="p-1.5 bg-white dark:bg-purple-800 rounded-full text-gray-900 dark:text-purple-300 hover:text-red-500 shadow-sm" title="Delete"><SafeIcon icon={FiIcons.FiTrash2} className="text-xs"/></button>
                              </div>
                              <div className="bg-purple-600 text-white px-6 py-4 rounded-2xl rounded-tr-sm shadow-xl font-medium border border-purple-500/50 relative">
                                {entry.bookmarked && <SafeIcon icon={FiIcons.FiBookmark} className="absolute top-2 left-2 text-amber-300 fill-current text-xs opacity-80" title="Bookmarked" />}
                                <p className="leading-relaxed whitespace-pre-wrap">{entry.content}</p>
                              </div>
                           </>
                         )}
                    </div>
                  )
                ) : (
                    <div className="w-full group relative">
                      {editingMessageId === entry.id ? (
                        <div className="flex flex-col items-start gap-2 w-full max-w-3xl">
                          <textarea
                            value={editContent} onChange={(e) => setEditContent(e.target.value)}
                            className={`w-full bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 p-4 rounded-xl border border-purple-300 dark:border-purple-700 focus:outline-none focus:ring-1 focus:ring-amber-500 ${narrativeStyle === 'writer' ? 'font-serif text-xl' : 'italic text-lg'}`}
                            rows={Math.max(3, editContent.split('\n').length)}
                          />
                          <div className="flex space-x-3">
                            <button onClick={() => { viewingArchiveChapter ? editArchivedEntry(viewingArchiveChapter.id, entry.id, editContent) : editStoryEntry(entry.id, editContent); setEditingMessageId(null); }} className="btn-primary text-xs py-1 px-3">Save</button>
                            <button onClick={() => setEditingMessageId(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="absolute top-0 left-0 -translate-y-full flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setEditContent(entry.content); setEditingMessageId(entry.id); }} className="p-1 text-gray-900 dark:text-purple-400 hover:text-amber-500" title="Edit"><SafeIcon icon={FiIcons.FiEdit2} className="text-xs"/></button>
                            <button onClick={() => deleteStoryEntry(entry.id)} className="p-1 text-gray-900 dark:text-purple-400 hover:text-red-500" title="Delete"><SafeIcon icon={FiIcons.FiTrash2} className="text-xs"/></button>
                          </div>
                          <p className={`whitespace-pre-wrap ${narrativeStyle === 'writer' ? 'text-xl md:text-2xl text-gray-800 dark:text-gray-200 leading-loose' : 'text-xl text-gray-800 dark:text-gray-200 italic'}`}>
                            {entry.content}
                          </p>
                        </>
                      )}
                  </div>
                )}
              </motion.div>
            ))}
            {isGenerating && !isAiPausedForUser && (
              <div className="flex items-center space-x-3 text-amber-500 font-serif italic">
                <SafeIcon icon={FiIcons.FiFeather} className="animate-bounce" />
                <span className="text-sm">The Weaver is weaving fate...</span>
              </div>
            )}
            {sceneImage && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center my-6">
                 <img
                   src={sceneImage.url}
                   alt="Scene Visualization"
                   className="rounded-2xl shadow-2xl max-w-full h-auto border-4 border-gray-200 dark:border-purple-800 bg-gray-100 dark:bg-purple-900 min-h-[200px] min-w-[200px]"
                   onError={(e) => {
                     e.target.onerror = null;
                     e.target.src = "https://placehold.co/1024x1024/2d1b4e/a78bfa?text=Visualization+Failed\\nTry+Again";
                   }}
                 />
                 <div className="flex space-x-4 mt-4">
                   <button
                     onClick={() => {
                       addToGallery({
                         url: sceneImage.url,
                         baseScene: sceneImage.baseScene,
                         modifications: 0,
                         timestamp: new Date().toISOString()
                       });
                       setSceneImage(null);
                     }}
                     className="btn-primary flex items-center space-x-2 text-sm"
                   >
                     <SafeIcon icon={FiIcons.FiSave} />
                     <span>Save to Gallery</span>
                   </button>
                   <button
                     onClick={() => setSceneImage(null)}
                     className="btn-secondary flex items-center space-x-2 text-sm"
                   >
                     <SafeIcon icon={FiIcons.FiX} />
                     <span>Dismiss</span>
                   </button>
                 </div>
              </motion.div>
            )}
            {isGeneratingImage && (
               <div className="flex items-center justify-center space-x-3 text-amber-500 font-serif italic my-6">
                 <SafeIcon icon={FiIcons.FiImage} className="animate-pulse" />
                 <span className="text-sm">Visualizing the scene...</span>
               </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {narrativeStyle === 'adventurer' && <CombatOverlay />}

        {/* Action Panel */}
        <div className="relative bottom-0 left-0 right-0 p-8 pt-4 bg-white dark:bg-purple-950 shadow-[0_-10px_40px_rgba(0,0,0,0.05)] dark:shadow-[0_-10px_40px_rgba(0,0,0,0.5)] border-t border-gray-100 dark:border-purple-900">
          <div className="max-w-3xl mx-auto space-y-4">
            {!user ? (
               <div className="text-center pb-4 space-y-4">
                 <p className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 p-3 rounded-lg text-sm font-bold shadow-md max-w-lg mx-auto border border-amber-200 dark:border-amber-800">
                   You must be logged in to write.
                 </p>
                 <button
                   onClick={() => navigate('/auth')}
                   className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-bold shadow-md transition-colors inline-flex items-center space-x-2"
                 >
                   <SafeIcon icon={FiIcons.FiUser} />
                   <span>Sign up for a free account</span>
                 </button>
               </div>
            ) : viewingArchiveChapter ? (
               <div className="text-center pb-4 space-y-4">
                 <p className="bg-slate-800/80 text-white p-3 rounded-lg text-xs italic shadow-md max-w-lg mx-auto">You are currently viewing an archived chapter. Return to the current session to continue writing.</p>
                 <button
                   onClick={() => setViewingArchiveChapter(null)}
                   className="px-6 py-2 bg-purple-600 hover:bg-gray-500 text-white rounded-full text-sm font-bold shadow-md transition-colors inline-flex items-center space-x-2"
                 >
                   <SafeIcon icon={FiIcons.FiArrowLeft} />
                   <span>Return to Active Story</span>
                 </button>
               </div>
            ) : (
              <>
            {!isReaderMode && (
              <AnimatePresence>
                {showTools && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    className="overflow-hidden flex flex-col space-y-3 bg-gray-50 dark:bg-purple-900/40 p-4 rounded-2xl border border-gray-100 dark:border-purple-800"
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      {narrativeStyle === 'adventurer' ? (
                        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                          <span className="text-[10px] font-black text-amber-600 uppercase py-2 mr-2">Choices:</span>
                          {(ai?.suggestions || []).map((suggestion, i) => (
                            <button
                              key={i} disabled={isGenerating} onClick={() => handleSend(suggestion)}
                              className="px-4 py-1.5 bg-gray-100 dark:bg-purple-900/60 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-gray-900 dark:text-purple-200 text-xs rounded-full border border-gray-200 dark:border-purple-800 transition-all font-medium"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      ) : <div />}
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 dark:border-purple-800 pt-3 mt-2 w-full gap-2 flex-wrap sm:flex-nowrap">
                      <div className="flex items-center space-x-1.5 flex-wrap gap-y-2">
                        <button
                          onClick={toggleAiPause}
                          className={`px-3 py-1 text-[10px] rounded-full border transition-all font-bold flex items-center space-x-1 ${isAiPausedForUser ? 'bg-red-100 hover:bg-red-200 dark:bg-red-900/40 dark:hover:bg-red-900/60 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' : 'bg-green-100 hover:bg-green-200 dark:bg-green-900/40 dark:hover:bg-green-900/60 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'}`}
                          title={isAiPausedForUser ? "AI is Paused - Click to Unpause" : "AI is Active - Click to Pause"}
                        >
                          <SafeIcon icon={isAiPausedForUser ? FiIcons.FiPauseCircle : FiIcons.FiPlayCircle} />
                          <span>{isAiPausedForUser ? 'AI Paused' : 'AI Active'}</span>
                        </button>
                        <span className="text-[9px] px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-full font-bold border border-amber-200 dark:border-amber-900/40">
                          {ai?.settings?.tone}
                        </span>
                        <span className="text-[9px] px-2 py-1 bg-gray-50 dark:bg-purple-900/40 text-gray-900 dark:text-purple-300 rounded-full font-bold border border-gray-200 dark:border-purple-800">
                          {ai?.settings?.responseLength}
                        </span>
                        {ai?.imageGenerator?.enabled && (story || []).length > 0 && !sceneImage && !isGeneratingImage && (
                          <button
                            onClick={async () => {
                              setIsGeneratingImage(true);
                              try {
                                const lastStoryEntry = story[story.length - 1].content;
                                const url = await generateImage(lastStoryEntry, character, ai.imageGenerator.style, state.gallery);
                                setSceneImage({ url, baseScene: lastStoryEntry });
                              } catch (e) {
                                console.error('Image generation failed', e);
                              } finally {
                                setIsGeneratingImage(false);
                              }
                            }}
                            className="px-3 py-1 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-[10px] rounded-full border border-amber-300 dark:border-amber-700 transition-all font-bold flex items-center space-x-1"
                          >
                            <SafeIcon icon={FiIcons.FiImage} />
                            <span>Visualize</span>
                          </button>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        <label className={`flex items-center justify-center p-1.5 rounded-full transition-colors backdrop-blur-sm shadow-sm border text-xs cursor-pointer ${!canExport ? 'bg-gray-100 text-gray-400 opacity-50 border-gray-200' : 'bg-white/50 dark:bg-purple-950/50 hover:bg-gray-50 dark:hover:bg-purple-900/30 text-gray-900 dark:text-purple-400 border-gray-200 dark:border-purple-800'}`} title="Import Story">
                          <SafeIcon icon={!canExport ? FiIcons.FiLock : FiIcons.FiUpload} />
                          <input type="file" accept=".json,.txt,.csv,application/json,text/plain,text/csv" onChange={handleImport} className="hidden" disabled={!canExport} />
                        </label>
                        <div className="relative">
                          <button
                            onClick={() => canExport ? setShowExportMenu(!showExportMenu) : alert("Story Export & Cloud Sync is available on the Storyteller tier and above. Please upgrade to export your story.")}
                            className={`flex items-center justify-center p-1.5 rounded-full transition-colors backdrop-blur-sm shadow-sm border text-xs ${!canExport ? 'bg-gray-100 text-gray-400 opacity-50 border-gray-200 cursor-not-allowed' : 'bg-white/50 dark:bg-purple-950/50 hover:bg-gray-50 dark:hover:bg-purple-900/30 text-gray-900 dark:text-purple-400 border-gray-200 dark:border-purple-800'}`}
                            title="Export Story"
                          >
                            <SafeIcon icon={!canExport ? FiIcons.FiLock : FiIcons.FiDownload} />
                          </button>

                          <AnimatePresence>
                            {showExportMenu && (
                              <motion.div
                                initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }}
                                className="absolute left-0 bottom-full mb-1 w-32 bg-white dark:bg-purple-950 border border-gray-200 dark:border-purple-800 rounded-xl shadow-lg overflow-hidden z-10"
                              >
                                <button onClick={() => handleExport('json')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors">Export JSON</button>
                                <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors border-t border-gray-100 dark:border-purple-800/50">Export CSV</button>
                                <button onClick={() => handleExport('xlsx')} className="w-full text-left px-4 py-2 text-xs text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900 transition-colors border-t border-gray-100 dark:border-purple-800/50">Export Excel</button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        {state.ownerId === user?.id && (
                          <>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to end this chapter? This will start a new chapter.')) {
                                  archiveCurrentStory(null, null);
                                  setViewingArchiveChapter(null);
                                }
                              }}
                              className="px-3 py-1.5 bg-white/50 dark:bg-purple-950/50 hover:bg-amber-50 dark:hover:bg-amber-900/30 text-gray-900 dark:text-purple-400 hover:text-amber-600 text-xs rounded-full border border-gray-200 dark:border-purple-800 transition-colors backdrop-blur-sm shadow-sm flex items-center space-x-1"
                              title="End Chapter"
                            >
                              <SafeIcon icon={FiIcons.FiBook} />
                              <span>End</span>
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Are you sure you want to start this chapter over? This will erase all unarchived entries.')) {
                                  startOverChapter();
                                }
                              }}
                              className="px-3 py-1.5 bg-white/50 dark:bg-purple-950/50 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-900 dark:text-purple-400 hover:text-red-500 text-xs rounded-full border border-gray-200 dark:border-purple-800 transition-colors backdrop-blur-sm shadow-sm flex items-center space-x-1"
                              title="Start Over Chapter"
                            >
                              <SafeIcon icon={FiIcons.FiRefreshCcw} />
                              <span>Reset</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="relative flex items-end bg-white dark:bg-purple-900 rounded-3xl border border-gray-200 dark:border-purple-800 shadow-2xl overflow-visible focus-within:border-amber-500 focus-within:ring-4 focus-within:ring-amber-500/10 transition-all"
            >
              {!isReaderMode && (
                <button
                  type="button"
                  onClick={() => setShowTools(!showTools)}
                  className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white dark:bg-purple-900 border border-gray-200 dark:border-purple-800 text-gray-600 hover:text-amber-500 rounded-full p-2 px-4 shadow-sm transition-colors z-10"
                  title="Toggle Narrative Tools"
                >
                  <SafeIcon icon={showTools ? FiIcons.FiChevronDown : FiIcons.FiChevronUp} className="text-xl" />
                </button>
              )}
              {narrativeStyle === 'adventurer' && (
                <div className="absolute left-2 bottom-6 z-10">
                  <DiceRoller
                    disabled={isGenerating}
                    onRoll={(value) => setInput(prev => `${prev ? prev + ' ' : ''}[Rolled: ${value}] `)}
                  />
                </div>
              )}
              <textarea
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); } }}
                placeholder={narrativeStyle === 'writer' ? '🖊️...' : 'What is your next move?'}
                className={`flex-1 max-h-32 min-h-[50px] bg-transparent ${narrativeStyle === 'adventurer' ? 'pl-14 pr-24' : 'pl-8 pr-24'} py-4 focus:outline-none resize-none text-gray-900 dark:text-white font-medium text-lg placeholder:text-gray-900 dark:placeholder:text-gray-500`}
                rows="1" disabled={isGenerating}
                style={{ WebkitTextFillColor: 'inherit' }}
              />
              <div className="absolute right-2 bottom-3 flex items-center space-x-1">
                <div className="relative">
                  <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="p-3 text-gray-400 hover:text-amber-500 transition-colors">
                    <SafeIcon icon={FiIcons.FiSmile} className="text-xl" />
                  </button>
                  <AnimatePresence>
                    {showEmojiPicker && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute bottom-full right-0 mb-2 z-50">
                        <EmojiPicker onEmojiClick={(e) => { setInput(prev => prev + e.emoji); setShowEmojiPicker(false); }} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <button type="submit" disabled={!input.trim() || isGenerating} className="p-3 text-amber-600 hover:text-amber-500 disabled:opacity-20 transition-all">
                  <SafeIcon icon={FiIcons.FiSend} className="text-2xl" />
                </button>
              </div>
            </form>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryView;